import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { oauthApi } from '../services/oauthApi';
import type {
  OAuthProvider,
  OAuthConfig,
  AvailableProvider,
  CreateOAuthConfigRequest,
  UpdateOAuthConfigRequest,
  BulkImportConfig,
} from '../types/oauth';

// Query keys
export const oauthKeys = {
  all: ['oauth-configs'] as const,
  lists: () => [...oauthKeys.all, 'list'] as const,
  list: (projectId: string) => [...oauthKeys.lists(), projectId] as const,
  details: () => [...oauthKeys.all, 'detail'] as const,
  detail: (projectId: string, provider: OAuthProvider) =>
    [...oauthKeys.details(), projectId, provider] as const,
  redirectUri: (projectId: string, provider: OAuthProvider) =>
    [...oauthKeys.all, 'redirect-uri', projectId, provider] as const,
};

// ============ Query Hooks ============

export function useOAuthConfigs(projectId: string | undefined) {
  return useQuery({
    queryKey: oauthKeys.list(projectId!),
    queryFn: async () => {
      const response = await oauthApi.list(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch OAuth configurations');
      }
      return response.data!;
    },
    enabled: !!projectId,
  });
}

export function useOAuthConfig(projectId: string | undefined, provider: OAuthProvider | undefined) {
  return useQuery({
    queryKey: oauthKeys.detail(projectId!, provider!),
    queryFn: async () => {
      const response = await oauthApi.get(projectId!, provider!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch OAuth configuration');
      }
      return response.data!;
    },
    enabled: !!projectId && !!provider,
  });
}

export function useOAuthRedirectUri(projectId: string | undefined, provider: OAuthProvider | undefined) {
  return useQuery({
    queryKey: oauthKeys.redirectUri(projectId!, provider!),
    queryFn: async () => {
      const response = await oauthApi.getRedirectUri(projectId!, provider!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch redirect URI');
      }
      return response.data!;
    },
    enabled: !!projectId && !!provider,
  });
}

// ============ Mutation Hooks ============

export function useCreateOAuthConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateOAuthConfigRequest;
    }) => {
      const response = await oauthApi.create(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create OAuth configuration');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.list(variables.projectId) });
    },
  });
}

export function useUpdateOAuthConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      provider,
      data,
    }: {
      projectId: string;
      provider: OAuthProvider;
      data: UpdateOAuthConfigRequest;
    }) => {
      const response = await oauthApi.update(projectId, provider, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update OAuth configuration');
      }
      return { config: response.data!, warning: response.warning };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.list(variables.projectId) });
      queryClient.invalidateQueries({
        queryKey: oauthKeys.detail(variables.projectId, variables.provider),
      });
    },
  });
}

export function useDeleteOAuthConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      provider,
    }: {
      projectId: string;
      provider: OAuthProvider;
    }) => {
      const response = await oauthApi.delete(projectId, provider);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete OAuth configuration');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.list(variables.projectId) });
      queryClient.removeQueries({
        queryKey: oauthKeys.detail(variables.projectId, variables.provider),
      });
    },
  });
}

export function useTestOAuthConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      provider,
    }: {
      projectId: string;
      provider: OAuthProvider;
    }) => {
      const response = await oauthApi.testConnection(projectId, provider);
      if (!response.success) {
        throw new Error(response.error || 'Connection test failed');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      // Invalidate to refresh the tested_at timestamp
      queryClient.invalidateQueries({ queryKey: oauthKeys.list(variables.projectId) });
      queryClient.invalidateQueries({
        queryKey: oauthKeys.detail(variables.projectId, variables.provider),
      });
    },
  });
}

export function useBulkImportOAuthConfigs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      configs,
    }: {
      projectId: string;
      configs: BulkImportConfig[];
    }) => {
      const response = await oauthApi.bulkImport(projectId, { configs });
      if (!response.success) {
        throw new Error(response.error || 'Bulk import failed');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: oauthKeys.list(variables.projectId) });
    },
  });
}

// ============ Helper Types ============

export interface OAuthConfigsData {
  configured: OAuthConfig[];
  available: AvailableProvider[];
}
